import React, { useEffect, useState, useCallback } from 'react';
import { isAuthenticated } from '../../../services/auth';
import InputMask from 'react-input-mask';
import { message, Popconfirm, Result, Spin } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import {
  singleProduct,
  updateProduct,
  addProduct,
  updateProductStatus,
} from '../../../services/product';
import { allLocations, allCategories } from '../../../services/admin';
import { useHistory, Link } from 'react-router-dom';
import UnifiedDashboard from '../../../components/layout/UnifiedDashboard';
import './UpsertProduct.css';

const API = (process.env.REACT_APP_API).replace(/\/api$/, '');

const UpsertProduct = ({ match, isModal = false, onClose, returnUrl = '/user/dashboard', hideBackButton = false }) => {
  const [values, setValues] = useState({
    name: '',
    category: '',
    location: '',
    description: '',
    condition: '',
    price: '',
    author: '',
    privateObservation: '',
    privatePhone: '',
    ownerName: '',
    ownerCpf: '',
    ownerAddress: '',
    ownerPhone: '',
    ownerWhatsapp: '',
  });
  const [images, setImages] = useState([]);
  const [images2, setImages2] = useState([]);
  const [locations, setLocations] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  const { user, token } = isAuthenticated();
  const { name, category, location, description, condition, price, author, privateObservation, privatePhone, ownerName, ownerCpf, ownerAddress, ownerPhone, ownerWhatsapp } = values;
  const history = useHistory();

  useEffect(() => {
    loadCategories();
    loadLocations();

    // Verificar se é modo edição ou criação
    if (match.params.productId) {
      setIsEditMode(true);
      loadProduct();
    } else {
      setIsEditMode(false);
      setLoading(false);
    }
  }, []);

  const loadCategories = async () => {
    let res = await allCategories();
    setCategories(res.data);
  };

  const loadLocations = async () => {
    let res = await allLocations();
    setLocations(res.data);
  };

  const loadProduct = async () => {
    setLoading(true);
    try {
      const res = await singleProduct(match.params.productId, token);
      let parsedPrivateObs = {};
      let legacyObs = res.data.privateObservation || '';
      try {
        if (legacyObs.trim().startsWith('{')) {
          parsedPrivateObs = JSON.parse(legacyObs);
          legacyObs = parsedPrivateObs.legacyObs || '';
        }
      } catch (e) {
        // Ignora erro se não for JSON válido (legado)
      }

      setValues({
        ...values,
        name: res.data.name,
        category: res.data.category._id,
        location: res.data.location._id,
        description: res.data.description,
        condition: res.data.condition,
        price: res.data.price ? new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(res.data.price) : '',
        author: res.data.author._id,
        privateObservation: legacyObs,
        privatePhone: res.data.privatePhone || '',
        ownerName: parsedPrivateObs.ownerName || '',
        ownerCpf: parsedPrivateObs.ownerCpf || '',
        ownerAddress: parsedPrivateObs.ownerAddress || '',
        ownerPhone: parsedPrivateObs.ownerPhone || '',
        ownerWhatsapp: parsedPrivateObs.ownerWhatsapp || '',
      });
      setImages(res.data.images);
      setImages2(res.data.images);
    } catch (err) {
      console.error(err);
      message.error('Erro ao carregar os dados do produto.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (name) => (e) => {
    setValues({ ...values, [name]: e.target.value });
  };

  const handlePriceChange = (e) => {
    let val = e.target.value;
    val = val.replace(/\D/g, '');
    if (val) {
      const numValue = parseInt(val, 10) / 100;
      val = new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(numValue);
    }
    setValues({ ...values, price: val });
  };

  const handleImageChange = (e) => {
    if (e.target.files) {
      const filesArray = Array.from(e.target.files);
      const newPreviews = filesArray.map((file) => URL.createObjectURL(file));

      setImages((prevImages) => [...prevImages, ...filesArray]);
      setImages2((prevPreviews) => [...prevPreviews, ...newPreviews]);
    }
  };

  const handleRemoveImage = (index) => {
    setImages((prevImages) => prevImages.filter((_, i) => i !== index));
    setImages2((prevPreviews) => {
      const previewToRemove = prevPreviews[index];

      if (typeof previewToRemove === 'string' && previewToRemove.startsWith('blob:')) {
        URL.revokeObjectURL(previewToRemove);
      }

      return prevPreviews.filter((_, i) => i !== index);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!name || !category || !location || !description || !price) {
      message.error('Preencha todos os campos obrigatórios', 4);
      return;
    }

    if (!isEditMode && images.length === 0) {
      message.error('Selecione pelo menos uma imagem', 4);
      return;
    }

    setLoading(true);

    let productData = new FormData();
    productData.append('name', name);
    productData.append('category', category);
    productData.append('location', location);
    productData.append('description', description);
    productData.append('condition', condition);
    
    let cleanPrice = price;
    if (typeof price === 'string') {
      cleanPrice = price.replace(/\./g, '').replace(',', '.');
    }
    productData.append('price', cleanPrice);
    const privateData = {
      ownerName,
      ownerCpf,
      ownerAddress,
      ownerPhone,
      ownerWhatsapp,
      legacyObs: privateObservation
    };
    productData.append('privateObservation', JSON.stringify(privateData));
    productData.append('privatePhone', privatePhone);

    try {
      if (isEditMode) {
        // Edição
        productData.append('author', author);
        productData.append('userAuth', user._id);

        images2.forEach((image) => {
          if (typeof image === 'string' && !image.startsWith('blob:')) {
            productData.append('existingImages', image);
          }
        });

        images.forEach((image) => {
          if (image instanceof File) {
            productData.append('images', image);
          }
        });

        await updateProduct(match.params.productId, productData, token);
        try {
          await updateProductStatus({ productId: match.params.productId, status: 'active' });
        } catch (statusErr) {
          console.error("Failed to set product active directly:", statusErr);
        }
        message.success('Produto atualizado com sucesso', 4);
      } else {
        // Criação
        images.forEach((image) => {
          if (image instanceof File) {
            productData.append('images', image);
          }
        });

        await addProduct(match.params.userId, productData, token);
        message.success('Produto adicionado com sucesso', 4);
      }

      if (isModal && onClose) {
        onClose();
      } else {
        history.push('/user/dashboard');
      }
    } catch (err) {
      console.log(err);
      if (err.response && err.response.status === 400) {
        message.error(err.response.data?.error || err.response.data || 'Erro ao salvar produto.', 4);
      } else {
        message.error('Erro ao salvar produto.', 4);
      }
    } finally {
      setLoading(false);
    }
  };

  const upsertProductForm = () => (
    <form onSubmit={handleSubmit} noValidate className='upsert-product-form'>
      <div className='form-group'>
        <label className='form-label'>Nome do Produto*</label>
        <input
          type='text'
          className='form-control'
          placeholder='Digite o nome do produto'
          value={name}
          onChange={handleChange('name')}
          disabled={loading}
        />
      </div>

      <div className='form-group'>
        <h6>Imagens*</h6>
        <div className='upsert-product-images-section'>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', alignItems: 'flex-start' }}>
            <label className='upsert-product-plus-btn' style={{ cursor: loading ? 'not-allowed' : 'pointer' }}>
              <i className='fas fa-plus'></i>
              <input
                onChange={handleImageChange}
                type='file'
                name='images'
                accept='image/*'
                multiple
                hidden
                disabled={loading}
              />
            </label>
            {images2 && images2.length > 0 && images2.map((image, i) => {
              const isBlob = typeof image === 'string' && image.startsWith('blob:');
              const imageUrl = isBlob ? image : `${API}${image}`;
              return (
                <div className='upsert-product-preview-container' key={i}>
                  <img
                    src={imageUrl}
                    alt='preview_image'
                    className='upsert-product-preview-img'
                  />
                  <Popconfirm
                    placement='top'
                    title={'Excluir imagem?'}
                    onConfirm={() => handleRemoveImage(i)}
                    okText='Sim'
                    cancelText='Não'
                    disabled={loading}
                  >
                    <i
                      className='fas fa-times-circle upsert-product-remove-icon'
                      role='button'
                    ></i>
                  </Popconfirm>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.75rem' }}>
        <div className='form-group'>
          <h6>Categoria*</h6>
          <select
            className='form-select'
            onChange={handleChange('category')}
            value={category}
            disabled={loading}
          >
            <option value=''>Selecione a categoria</option>
            {categories.map((c, i) => (
              <option key={i} value={c._id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
        <div className='form-group'>
          <h6>Localização*</h6>
          <select
            className='form-select'
            onChange={handleChange('location')}
            value={location}
            disabled={loading}
          >
            <option value=''>Selecione a localização</option>
            {locations.map((l, i) => (
              <option key={i} value={l._id}>
                {l.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className='form-group'>
        <label className='form-label'>Descrição*</label>
        <textarea
          className='form-control upsert-product-description'
          placeholder='Digite uma descrição detalhada do produto'
          value={description}
          onChange={handleChange('description')}
          disabled={loading}
          rows={15}
          style={{ minHeight: '300px', resize: 'vertical' }}
        />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.75rem' }}>
        <div className='form-group'>
          <h6>Condição</h6>
          <select
            className='form-select'
            onChange={handleChange('condition')}
            value={condition}
            disabled={loading}
          >
            <option value=''>Selecione a condição</option>
            <option value='not_applicable'>Não se aplica</option>
            <option value='new'>Novo</option>
            <option value='used'>Usado</option>
          </select>
        </div>
        <div className='form-group'>
          <label className='form-label'>Preço*</label>
          <div className='input-group'>
            <span className='input-group-text'>R$</span>
            <input
              type='text'
              className='form-control'
              placeholder='0,00'
              value={price}
              onChange={handlePriceChange}
              disabled={loading}
            />
          </div>
        </div>
      </div>

      <div style={{ padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '8px', marginBottom: '1.5rem', border: '1px solid #e2e8f0' }}>
        <h5 style={{ marginBottom: '15px', color: '#333' }}><i className="fas fa-lock me-2"></i>Informações Privadas do Proprietário</h5>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem' }}>
          <div className='form-group mb-0'>
            <label className='form-label'>Nome do Proprietário</label>
            <input
              type='text'
              className='form-control'
              placeholder='Nome do dono'
              value={ownerName}
              onChange={handleChange('ownerName')}
              disabled={loading}
            />
          </div>
          <div className='form-group mb-0'>
            <label className='form-label'>CPF (Opcional)</label>
            <InputMask
              mask='999.999.999-99'
              value={ownerCpf}
              onChange={handleChange('ownerCpf')}
              disabled={loading}
            >
              {(inputProps) => <input {...inputProps} type='text' className='form-control' placeholder='000.000.000-00' />}
            </InputMask>
          </div>
        </div>

        <div className='form-group mt-3'>
          <label className='form-label'>Endereço Completo</label>
          <input
            type='text'
            className='form-control'
            placeholder='Rua, Número, Bairro, Cidade'
            value={ownerAddress}
            onChange={handleChange('ownerAddress')}
            disabled={loading}
          />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem', marginTop: '1rem' }}>
          <div className='form-group mb-0'>
            <label className='form-label'>Telefone</label>
            <InputMask
              mask='(99) 99999-9999'
              value={ownerPhone}
              onChange={handleChange('ownerPhone')}
              disabled={loading}
            >
              {(inputProps) => <input {...inputProps} type='text' className='form-control' placeholder='(00) 00000-0000' />}
            </InputMask>
          </div>
          <div className='form-group mb-0'>
            <label className='form-label'>WhatsApp</label>
            <InputMask
              mask='(99) 99999-9999'
              value={ownerWhatsapp}
              onChange={handleChange('ownerWhatsapp')}
              disabled={loading}
            >
              {(inputProps) => <input {...inputProps} type='text' className='form-control' placeholder='(00) 00000-0000' />}
            </InputMask>
          </div>
        </div>

        {privateObservation && (
          <div className='form-group mt-3 mb-0'>
            <label className='form-label'>Observação Privada Antiga (Legado)</label>
            <textarea
              className='form-control'
              value={privateObservation}
              onChange={handleChange('privateObservation')}
              disabled={loading}
            />
          </div>
        )}
      </div>

      <div className='upsert-product-actions'>
        <button
          type='submit'
          className='upsert-product-submit-btn'
          disabled={loading}
        >
          {loading ? (
            <>
              <Spin size='small' style={{ marginRight: '8px' }} />
              Processando...
            </>
          ) : (
            isEditMode ? 'Atualizar Produto' : 'Anunciar Produto'
          )}
        </button>
      </div>
    </form>
  );

  if (loading && isEditMode) {
    return (
      <div className='upsert-product-loading'>
        <Spin size='large' />
      </div>
    );
  }

  if (isEditMode && author && author !== user._id) {
    return (
      <UnifiedDashboard>
        <Result
          status='403'
          title='403'
          subTitle='Desculpe, você não está autorizado a acessar esta página.'
          extra={
            <Link to='/' className='upsert-product-submit-btn' style={{ display: 'inline-block', textDecoration: 'none' }}>
              Voltar para Home
            </Link>
          }
        />
      </UnifiedDashboard>
    );
  }

  return (
    <>
      {isModal ? (
        upsertProductForm()
      ) : (
        <UnifiedDashboard>
          <div className='upsert-product-container'>
            <div className='upsert-product-header-section'>
              {!hideBackButton && (
                <button
                  className='upsert-product-back-btn-header'
                  onClick={() => history.goBack()}
                  title="Voltar"
                >
                  <ArrowLeftOutlined />
                </button>
              )}
              <h2 className='upsert-product-title'>
                <i className='fas fa-tags'></i>
                {isEditMode ? 'Editar Produto' : 'Anunciar Produto'}
              </h2>
              <p className='upsert-product-subtitle'>{isEditMode ? 'Atualize as informações do seu produto' : 'Crie um novo anúncio'}</p>
            </div>
            <div className='upsert-product-form-wrapper'>
              {upsertProductForm()}
            </div>
          </div>
        </UnifiedDashboard>
      )}
    </>
  );
};

export default UpsertProduct;
